import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { useAuth } from '@/hooks/useAuth';
import api from '@/services/api';
import type { ApiShippingSetting } from '@/types/api';

export default function CheckoutPage() {
  const { items, total, clearCart, removeFromCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [placed, setPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [whatsapp, setWhatsapp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toastMsg, setToastMsg] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<'pickup' | 'delivery'>('delivery');
  const [shippingSetting, setShippingSetting] = useState<ApiShippingSetting | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postcode, setPostcode] = useState('');
  const [saveAddress, setSaveAddress] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardDiscount, setGiftCardDiscount] = useState(0);
  const [giftCardValid, setGiftCardValid] = useState(false);
  const [giftCardChecking, setGiftCardChecking] = useState(false);
  const [giftCardMsg, setGiftCardMsg] = useState('');
  const [savedAddresses, setSavedAddresses] = useState<any[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);

  useEffect(() => {
    api.get('/settings').then(res => {
      if (res.data?.shipping) setShippingSetting(res.data.shipping);
      if (res.data?.contact_info?.whatsapp) setWhatsapp(res.data.contact_info.whatsapp);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length === 0) return;
    const productIds = items.map(item => Number(item.product.id));
    api.post('/cart/validate', { product_ids: productIds }).then(res => {
      const unavailable: number[] = res.data.unavailable || [];
      if (unavailable.length > 0) {
        const removedNames = items
          .filter(i => unavailable.includes(Number(i.product.id)))
          .map(i => i.product.name)
          .join('، ');
        setToastMsg(`تم حذف من السلة لعدم توفره: ${removedNames}`);
        unavailable.forEach(pid => {
          const item = items.find(i => Number(i.product.id) === pid);
          if (item) removeFromCart(item.product.id);
        });
        setTimeout(() => setToastMsg(''), 5000);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      api.get('/addresses').then(res => setSavedAddresses(res.data || [])).catch(() => {});
      setEmail(user.email || '');
      setPhone(user.phone || '');
      const parts = (user.name || '').split(' ');
      if (parts.length >= 2) { setFirstName(parts[0]); setLastName(parts.slice(1).join(' ')); }
      else setFirstName(user.name || '');
    } else {
      try {
        const saved = localStorage.getItem('souad_guest_address');
        if (saved) {
          const addr = JSON.parse(saved);
          setFirstName(addr.firstName || ''); setLastName(addr.lastName || '');
          setPhone(addr.phone || ''); setEmail(addr.email || '');
          setAddress(addr.address || ''); setCity(addr.city || '');
          setPostcode(addr.postcode || ''); setSaveAddress(true);
        }
      } catch {}
    }
  }, [user]);

  const selectSavedAddress = (addr: any) => {
    setSelectedAddressId(addr.id);
    setFirstName(addr.first_name || ''); setLastName(addr.last_name || '');
    setPhone(addr.phone || ''); setAddress(addr.address || '');
    setCity(addr.city || ''); setPostcode(addr.postcode || '');
    setSaveAddress(true);
  };
  const handleFieldChange = (setter: (v: string) => void, value: string) => { setter(value); if (selectedAddressId) setSelectedAddressId(null); };
  const validateGiftCard = async () => {
    if (!giftCardCode.trim()) return;
    setGiftCardChecking(true); setGiftCardMsg('');
    try {
      const res = await api.post('/giftcard/validate', { code: giftCardCode.trim() });
      setGiftCardValid(true); setGiftCardDiscount(res.data.amount);
      setGiftCardMsg(`تم تطبيق بطاقة هدية بقيمة ${res.data.amount} شيكل`);
    } catch (err: any) {
      setGiftCardValid(false); setGiftCardDiscount(0);
      setGiftCardMsg(err.response?.data?.message || 'بطاقة غير صالحة');
    } finally { setGiftCardChecking(false); }
  };

  const threshold = Number(shippingSetting?.free_shipping_threshold) || 300;
  const shipCost = Number(shippingSetting?.shipping_cost) || 0;
  const shippingCost = deliveryMethod === 'delivery' ? (total >= threshold ? 0 : shipCost) : 0;
  const finalTotal = Number(total) + Number(shippingCost);
  const afterGiftCard = Math.max(0, finalTotal - Number(giftCardDiscount));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault(); setError('');
    if (items.length === 0) return;
    if (!firstName || !lastName || !phone || !email) { setError('الاسم ورقم الهاتف والبريد الإلكتروني مطلوبة'); return; }
    if (deliveryMethod === 'delivery' && (!address || !city)) { setError('العنوان والمدينة مطلوبان للتوصيل'); return; }
    if (paymentMethod === 'gift_card') {
      if (!giftCardValid) { setError('يرجى التحقق من كود بطاقة الهدية'); return; }
      if (afterGiftCard > 0) { setError('رصيد بطاقة الهدية لا يكفي. اختر طريقة دفع إضافية للمبلغ المتبقي'); return; }
    }
    setLoading(true);
    try {
      const payload: any = {
        items: items.map(item => ({ product_id: Number(item.product.id), quantity: item.quantity, price: item.product.price })),
        delivery_method: deliveryMethod, method_paid: paymentMethod, shipping_cost: shippingCost,
      };
      if (deliveryMethod === 'delivery') {
        if (selectedAddressId) { payload.address_id = selectedAddressId; }
        else { payload.shipping_address = { first_name: firstName, last_name: lastName, phone, address, city, postcode, country: 'فلسطين' }; }
        payload.save_address = saveAddress;
      }
      payload.guest_name = `${firstName} ${lastName}`; payload.guest_phone = phone; payload.guest_email = email;
      if (giftCardValid) payload.gift_card_code = giftCardCode.trim();
      if (saveAddress && !user) { localStorage.setItem('souad_guest_address', JSON.stringify({ firstName, lastName, phone, email, address, city, postcode })); }
      const res = await api.post('/checkout', payload);
      const od = res.data.order;
      const token = res.data.payment_token;
      setOrderNumber(od?.order_number || '');
      setOrderDetails(od || null);
      if (paymentMethod === 'credit_card' && token) navigate(`/payment/${token}`);
      else { setPlaced(true); clearCart(); }
    } catch (err: any) { setError(err.response?.data?.message || 'حدث خطأ أثناء تقديم الطلب'); }
    finally { setLoading(false); }
  };

  if (placed) {
    const waNumber = whatsapp.replace(/\D/g, '') || '972590000000';
    const itemsText = (orderDetails?.order_items || [])
      .map((it: any) => `• ${it.product_name || 'منتج'} × ${it.quantity}`)
      .join('\n');
    const message = [
      'مرحباً، أود تأكيد طلبي ✅',
      `رقم الطلب: ${orderNumber}`,
      '',
      'تفاصيل الطلب:',
      itemsText,
      `الإجمالي: ${orderDetails?.total_cost ?? ''} شيكل`,
      '',
      'تم تأكيد الطلب.',
    ].filter(Boolean).join('\n');
    const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`;
    const isCash = paymentMethod === 'cash';

    return (
      <main className="min-h-screen flex items-center justify-center py-16 bg-cream-50">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6"><i className="ri-check-line text-3xl text-green-600" /></div>
          <h1 className="text-2xl font-bold text-charcoal-900 mb-3">تم تأكيد طلبك!</h1>
          {orderNumber && <p className="text-charcoal-500 mb-1 text-sm">رقم الطلب: <span className="font-mono font-bold">{orderNumber}</span></p>}
          <p className="text-charcoal-600 mb-6 text-sm">شكراً لتسوقك معنا.</p>
          <Link to="/shop" className="btn-primary inline-block">مواصلة التسوق</Link>
        </div>

        {isCash && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
            <div className="bg-white rounded-3xl max-w-md w-full p-8 text-center shadow-2xl">
              <div className="w-16 h-16 rounded-full bg-[#79A286]/15 flex items-center justify-center mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-4xl text-[#79A286]" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-[#79A286] mb-2">تم استلام طلبك بنجاح</h2>
              {orderNumber && (
                <p className="text-sm text-charcoal-500 mb-4">رقم الطلب: <span className="font-mono font-bold text-charcoal-900">{orderNumber}</span></p>
              )}
              <p className="text-sm md:text-base text-charcoal-900 font-bold leading-relaxed mb-6">
                هام جداً: لمساعدتنا على تجهيز وتوصيل طلبك بأسرع وقت، يرجى الضغط على زر التأكيد الفوري
              </p>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full bg-[#79A286] hover:bg-[#6a9177] text-white px-6 py-4 rounded-xl text-sm md:text-base font-bold transition-colors"
              >
                <i className="ri-whatsapp-line text-lg" />
                تأكيد فوري برسالة عالواتساب
              </a>
            </div>
          </div>
        )}
      </main>
    );
  }
  if (items.length === 0) return (
    <main className="min-h-screen flex items-center justify-center py-16"><div className="text-center"><div className="w-20 h-20 rounded-full bg-cream-200 flex items-center justify-center mx-auto mb-4"><i className="ri-shopping-bag-line text-3xl text-charcoal-400" /></div><h1 className="text-xl font-bold text-charcoal-900 mb-3">السلة فارغة</h1><Link to="/shop" className="btn-primary inline-block">تسوّقي الآن</Link></div></main>
  );

  return (
    <main className="min-h-screen py-8 md:py-12 bg-cream-50"><div className="container-main"><h1 className="heading-lg mb-8 text-center">إتمام الشراء</h1></div>
      {toastMsg && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 text-red-700 px-6 py-3 rounded-xl shadow-lg text-sm font-medium animate-bounce">{toastMsg}</div>
      )}
      <div className="container-main">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div className="order-2 lg:order-1">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-cream-300 p-6 md:p-8">
              <h2 className="text-lg font-bold text-charcoal-900 mb-4">طريقة الاستلام</h2>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button type="button" onClick={() => setDeliveryMethod('pickup')} className={`p-4 rounded-xl border text-sm font-medium transition-colors ${deliveryMethod === 'pickup' ? 'border-charcoal-900 bg-charcoal-50' : 'border-cream-300 bg-cream-50 hover:border-cream-400'}`}><i className="ri-store-2-line text-xl block mb-1" />استلام من المتجر</button>
                <button type="button" onClick={() => setDeliveryMethod('delivery')} className={`p-4 rounded-xl border text-sm font-medium transition-colors ${deliveryMethod === 'delivery' ? 'border-charcoal-900 bg-charcoal-50' : 'border-cream-300 bg-cream-50 hover:border-cream-400'}`}><i className="ri-truck-line text-xl block mb-1" />توصيل للمنزل</button>
              </div>
              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-6 text-right">{error}</div>}
              <h2 className="text-lg font-bold text-charcoal-900 mb-4">معلومات التواصل</h2>
              {user && savedAddresses.length > 0 && (
                <div className="mb-4 space-y-2">
                  <p className="text-xs text-charcoal-500 mb-1">العناوين المحفوظة (اختياري)</p>
                  {savedAddresses.map(addr => (
                    <button key={addr.id} type="button" onClick={() => selectSavedAddress(addr)} className={`block w-full text-right p-3 rounded-xl border text-sm transition-colors ${selectedAddressId === addr.id ? 'border-charcoal-900 bg-charcoal-50' : 'border-cream-300 hover:border-cream-400'}`}><p className="font-bold text-charcoal-900">{addr.first_name} {addr.last_name} — <span dir="ltr">{addr.phone}</span></p><p className="text-charcoal-500 text-xs">{addr.address} - {addr.city}</p></button>
                  ))}
                </div>
              )}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-charcoal-700 mb-1">الاسم الأول *</label><input type="text" value={firstName} onChange={e => handleFieldChange(setFirstName, e.target.value)} required className="w-full bg-cream-50 border border-cream-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blush-200" /></div>
                  <div><label className="block text-sm font-medium text-charcoal-700 mb-1">الاسم الأخير *</label><input type="text" value={lastName} onChange={e => handleFieldChange(setLastName, e.target.value)} required className="w-full bg-cream-50 border border-cream-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blush-200" /></div>
                </div>
                <div><label className="block text-sm font-medium text-charcoal-700 mb-1">رقم الهاتف *</label><input type="tel" value={phone} onChange={e => handleFieldChange(setPhone, e.target.value.replace(/\D/g, '').slice(0, 10))} required dir="ltr" placeholder="05XXXXXXXX" className="w-full bg-cream-50 border border-cream-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blush-200" /></div>
                <div><label className="block text-sm font-medium text-charcoal-700 mb-1">البريد الإلكتروني *</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} required dir="ltr" className="w-full bg-cream-50 border border-cream-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blush-200" /></div>
                {deliveryMethod === 'delivery' && (
                  <>
                    <div><label className="block text-sm font-medium text-charcoal-700 mb-1">العنوان الكامل *</label><input type="text" value={address} onChange={e => handleFieldChange(setAddress, e.target.value)} required className="w-full bg-cream-50 border border-cream-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blush-200" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-charcoal-700 mb-1">المدينة *</label><input type="text" value={city} onChange={e => handleFieldChange(setCity, e.target.value)} required className="w-full bg-cream-50 border border-cream-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blush-200" /></div>
                      <div><label className="block text-sm font-medium text-charcoal-700 mb-1">الرمز البريدي</label><input type="text" value={postcode} onChange={e => handleFieldChange(setPostcode, e.target.value)} className="w-full bg-cream-50 border border-cream-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blush-200" /></div>
                    </div>
                  </>
                )}
                <div className="flex items-center gap-2 pt-1"><input type="checkbox" id="saveAddr" checked={saveAddress} onChange={e => setSaveAddress(e.target.checked)} className="w-4 h-4 text-charcoal-900 rounded border-cream-300 focus:ring-charcoal-900" /><label htmlFor="saveAddr" className="text-sm text-charcoal-600 cursor-pointer">حفظ العنوان للمرات القادمة</label></div>
              </div>
              {(afterGiftCard > 0 || !giftCardValid) && (
                <>
                  <h2 className="text-lg font-bold text-charcoal-900 mb-4 mt-8">طريقة الدفع</h2>
                  <div className="space-y-3">
                    <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${paymentMethod === 'cash' ? 'border-charcoal-900 bg-charcoal-50' : 'bg-cream-50 border-cream-300 hover:bg-cream-100'}`}><input type="radio" name="payment" value="cash" checked={paymentMethod === 'cash'} onChange={e => setPaymentMethod(e.target.value)} className="w-4 h-4 accent-charcoal-900" /><div><span className="text-sm font-bold text-charcoal-900 block">الدفع عند الاستلام</span><span className="text-xs text-charcoal-500">ادفعي نقداً عند استلام الطلب</span></div></label>
                    <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${paymentMethod === 'credit_card' ? 'border-charcoal-900 bg-charcoal-50' : 'bg-cream-50 border-cream-300 hover:bg-cream-100'}`}><input type="radio" name="payment" value="credit_card" checked={paymentMethod === 'credit_card'} onChange={e => setPaymentMethod(e.target.value)} className="w-4 h-4 accent-charcoal-900" /><div><span className="text-sm font-bold text-charcoal-900 block">بطاقة ائتمان</span><span className="text-xs text-charcoal-500">Visa, MasterCard</span></div></label>
                  </div>
                </>
              )}

              <h2 className="text-lg font-bold text-charcoal-900 mb-4 mt-8">بطاقة هدايا</h2>
              <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors mb-4 ${paymentMethod === 'gift_card' ? 'border-charcoal-900 bg-charcoal-50' : 'bg-cream-50 border-cream-300 hover:bg-cream-100'}`}>
                <input type="radio" name="payment" value="gift_card" checked={paymentMethod === 'gift_card'} onChange={e => { setPaymentMethod(e.target.value); setGiftCardValid(false); setGiftCardMsg(''); }} className="w-4 h-4 accent-charcoal-900" />
                <div><span className="text-sm font-bold text-charcoal-900 block">الدفع ببطاقة هدية</span><span className="text-xs text-charcoal-500">استخدمي رصيد بطاقة الهدية</span></div>
              </label>
              <div className="flex gap-2"><input type="text" value={giftCardCode} onChange={e => { setGiftCardCode(e.target.value.toUpperCase()); setGiftCardValid(false); setGiftCardMsg(''); }} placeholder="أدخل كود بطاقة الهدية" className="flex-1 bg-cream-50 border border-cream-300 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:border-blush-200" /><button type="button" onClick={validateGiftCard} disabled={giftCardChecking || !giftCardCode.trim()} className="px-5 py-3 bg-charcoal-900 text-white rounded-xl text-sm font-medium hover:bg-charcoal-800 disabled:opacity-50 whitespace-nowrap">{giftCardChecking ? 'جاري...' : 'تحقق'}</button></div>
              {giftCardMsg && <div className={`mt-2 text-sm p-2 rounded-lg text-right ${giftCardValid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{giftCardMsg}{giftCardValid && <span className="block text-xs mt-1">الخصم: -{giftCardDiscount} شيكل</span>}</div>}
              {giftCardValid && afterGiftCard <= 0 && <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 text-right"><i className="ri-check-double-line ml-1" />بطاقة الهدية تغطي كامل المبلغ</div>}
              <button type="submit" disabled={loading} className="w-full btn-primary mt-8 text-center disabled:opacity-50">{loading ? 'جاري تأكيد الطلب...' : `تأكيد الطلب - ${afterGiftCard} شيكل`}</button>
            </form>
          </div>
          <div className="order-1 lg:order-2">
            <div className="bg-white rounded-2xl border border-cream-300 p-6 md:p-8 sticky top-24">
              <h2 className="text-lg font-bold text-charcoal-900 mb-6">ملخص الطلب</h2>
              <div className="space-y-4 mb-6">{items.filter((item, i, arr) => arr.findIndex(x => x.product.id === item.product.id) === i).map(item => (<div key={`${item.product.id}-${item.variant?.id ?? '0'}`} className="flex gap-4"><img src={item.product.image} alt={item.product.name} className="w-16 h-20 object-cover rounded-xl flex-shrink-0 bg-cream-100" /><div className="flex-1"><h3 className="text-sm font-medium text-charcoal-900">{item.product.name}</h3><p className="text-sm text-charcoal-600 mt-1">{item.product.price} شيكل x {item.quantity}</p></div><span className="text-sm font-bold text-charcoal-900">{item.product.price * item.quantity} شيكل</span></div>))}</div>
              <div className="border-t border-cream-300 pt-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-charcoal-500">المجموع الفرعي</span><span className="text-charcoal-900">{total} شيكل</span></div>
                <div className="flex justify-between text-sm"><span className="text-charcoal-500">طريقة الاستلام</span><span className="text-charcoal-700">{deliveryMethod === 'pickup' ? 'استلام من المتجر' : 'توصيل'}</span></div>
                <div className="flex justify-between text-sm"><span className="text-charcoal-500">الشحن</span><span className={shippingCost === 0 ? 'text-green-600' : 'text-charcoal-900'}>{shippingCost === 0 ? (deliveryMethod === 'pickup' ? '-' : `مجاني (فوق ${threshold} شيكل)`) : `${shippingCost} شيكل`}</span></div>
                {giftCardValid && <div className="flex justify-between text-sm"><span className="text-green-600">بطاقة هدية</span><span className="text-green-600">-{giftCardDiscount} شيكل</span></div>}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-cream-200"><span>الإجمالي</span><span>{afterGiftCard} شيكل</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
